import { cn } from '@app/lib/cn'

/**
 * AsortiGrid — the size-run strip.
 *
 * An asorti is a carton with a fixed size distribution (36×1 37×2 38×3 …). It is
 * the unit orders, production and stock are all counted in, so this strip shows up
 * on every module screen and has to stay legible at table-row scale.
 *
 * The form is borrowed from the size stamp on a shoe box: sizes sit small on a
 * paper band, quantities sit heavy underneath, one hairline per size. Two quiet
 * channels carry the rest: a 2px rule across the top of a column for status, and
 * a bottom-anchored ink wash sized to the quantity's share of the run — an asorti
 * is a curve (1 2 3 3 2 1), and the wash makes that curve legible without turning
 * the strip into a chart.
 */

export type AsortiStatus = 'neutral' | 'accent' | 'ok' | 'warn' | 'info'

export interface AsortiCell {
  /** Size — Korgun's BedKod or the displayed size number. */
  size: string | number
  /** Number of pairs. */
  qty: number
  /** Optional status tone (missing, completed, at risk…). */
  status?: AsortiStatus
}

export interface AsortiGridProps {
  cells: readonly AsortiCell[]
  /** Label shown above the strip. Without it only screen readers get one. */
  label?: string
  /** Total column. `true` sums the cells, a number is displayed as given. */
  total?: boolean | number
  density?: 'sm' | 'md'
  className?: string
}

const tint: Record<AsortiStatus, string> = {
  neutral: '',
  accent: 'bg-kordovan/6',
  ok: 'bg-zeytin/8',
  warn: 'bg-hardal/10',
  info: 'bg-celik/8',
}

/** 2px rule across the top of the column. */
const rule: Record<AsortiStatus, string> = {
  neutral: '',
  accent: 'shadow-[inset_0_2px_0_var(--color-kordovan)]',
  ok: 'shadow-[inset_0_2px_0_var(--color-zeytin)]',
  warn: 'shadow-[inset_0_2px_0_var(--color-hardal)]',
  info: 'shadow-[inset_0_2px_0_var(--color-celik)]',
}

const density = {
  sm: {
    head: 'h-4 min-w-8 px-1.5 text-[0.625rem]',
    body: 'h-6 px-1.5 text-data',
    /** Tallest the size-curve wash may grow, in px. */
    curve: 13,
    totalLabel: 'Top.',
  },
  md: {
    head: 'h-5 min-w-11 px-2 text-micro',
    body: 'h-8 px-2 text-[0.9375rem]',
    curve: 19,
    totalLabel: 'Toplam',
  },
} as const

const nf = new Intl.NumberFormat('tr-TR')

export function AsortiGrid({
  cells,
  label,
  total = false,
  density: densityProp = 'md',
  className,
}: AsortiGridProps) {
  const d = density[densityProp]
  const sum = cells.reduce((acc, cell) => acc + cell.qty, 0)
  const peak = cells.reduce((acc, cell) => Math.max(acc, cell.qty), 0)
  const totalValue = typeof total === 'number' ? total : sum
  const showTotal = total !== false

  return (
    <figure className={cn('inline-flex max-w-full flex-col gap-1', className)}>
      {label ? <figcaption className="eyebrow">{label}</figcaption> : null}

      <div className="max-w-full overflow-x-auto rounded-xs border border-hairline">
        <table className="border-collapse font-mono tnum">
          {!label && <caption className="sr-only">Asorti beden dağılımı</caption>}

          {/* Sizes: small, muted, on the paper band. */}
          <thead>
            <tr className="bg-kagit">
              {cells.map((cell, index) => (
                <th
                  key={`${cell.size}-${index}`}
                  scope="col"
                  className={cn(
                    'border-b border-hairline text-center font-normal text-ink-muted',
                    index > 0 && 'border-l border-hairline',
                    d.head,
                    tint[cell.status ?? 'neutral'],
                    rule[cell.status ?? 'neutral'],
                  )}
                >
                  {cell.size}
                </th>
              ))}
              {showTotal && (
                <th
                  scope="col"
                  className={cn(
                    'border-b border-l-2 border-hairline border-l-kosele/15 text-center',
                    'font-display font-semibold uppercase tracking-[0.08em] text-ink-muted',
                    d.head,
                  )}
                >
                  {d.totalLabel}
                </th>
              )}
            </tr>
          </thead>

          {/* Quantities: heavy, ink, on white. */}
          <tbody>
            <tr className="bg-surface">
              {cells.map((cell, index) => (
                <td
                  key={`${cell.size}-${index}`}
                  title={`Beden ${cell.size} · ${nf.format(cell.qty)} çift`}
                  className={cn(
                    'relative text-center font-medium text-ink',
                    index > 0 && 'border-l border-hairline',
                    d.body,
                    tint[cell.status ?? 'neutral'],
                    cell.qty === 0 && 'font-normal text-ink-muted/45',
                  )}
                >
                  {cell.qty > 0 && peak > 0 ? (
                    <span
                      aria-hidden
                      className="pointer-events-none absolute inset-x-0 bottom-0 bg-kosele/7"
                      style={{ height: `${Math.round(3 + (cell.qty / peak) * (d.curve - 3))}px` }}
                    />
                  ) : null}
                  <span className="relative">{cell.qty === 0 ? '·' : nf.format(cell.qty)}</span>
                </td>
              ))}
              {showTotal && (
                <td
                  className={cn(
                    'border-l-2 border-l-kosele/15 bg-kordovan/4 text-center font-semibold text-kordovan',
                    d.body,
                  )}
                >
                  {nf.format(totalValue)}
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </div>
    </figure>
  )
}
